// Library-mirroring layout shared by every local send panel — the ThemeProvider, two-column
// flex, right-hand preview pane and footer (Back/Save, the customisations.footerComponent slot,
// the invalid-message toast). Mirrors TemplateMessaging's own selected-template markup/classNames
// 1:1 (TemplateMessaging.tsx preview pane, Components/Footer) so Bloomreach's overrides.css keeps
// applying unchanged. Carries no template/carousel-shape knowledge: `leftClassName` and
// `previewConfig` are resolved by the caller and handed down as plain values.
import {
  Box, Button, ThemeProvider, Toast,
} from '@gupshup-frontend/gupshup-ts-components';

/**
 * @typedef {object} SendPanelShellProps
 * @property {import('react').ReactNode} children - left-column body, panel-specific fields
 * @property {object} previewConfig - JSON.stringify'd into `<gs-whatsapp-template-preview config>`
 * @property {number} [previewKey] - the preview's React key: a new value remounts the element, which starts over (an image
 *   that does not load goes back to its "Sample Media" placeholder). The library remounts its preview on a Save that passes
 *   its checks (TemplateMessaging.tsx:275-288), so a panel bumps this when its own Save does.
 * @property {() => void} onBack - Back button handler
 * @property {() => void} onSave - Save/Send button handler
 * @property {{ footerComponent?: import('react').ReactNode, sendButtonText?: string }} [customisations]
 * @property {boolean} [disabled] - disables the Save button
 * @property {boolean} showInvalidToast - opens the "Message missing or invalid" toast
 * @property {() => void} onCloseInvalidToast
 * @property {import('react').ReactNode} [footerExtra] - rendered between the two-column area and the footer (e.g. a testing note)
 * @property {string} [leftClassName] - className for the left Box; caller resolves the condition (e.g. nic-tm-sl-template-container)
 */

/**
 * @param {SendPanelShellProps} props
 * @returns {JSX.Element}
 */
const SendPanelShell = ({
  children, previewConfig, previewKey, onBack, onSave, customisations, disabled, showInvalidToast, onCloseInvalidToast, footerExtra,
  leftClassName,
}) => (
  <ThemeProvider>
    <div style={{ display: 'flex', gap: '24px' }} data-testid="bloomreach-send-panel">
      <Box
        maxWidth="xs"
        sx={{ marginTop: '12px', width: '100%', maxWidth: '400px' }}
        className={leftClassName}
      >
        {children}
      </Box>

      {/* Same wrapper/config shape as TemplateMessaging's own preview pane — the web component it
          registers is app-global, so it renders identically outside the library's own tree. */}
      <div style={{ marginTop: '10px', width: '100%' }}>
        <div style={{ height: '100%', maxWidth: '300px', position: 'relative' }}>
          <gs-whatsapp-template-preview key={previewKey} config={JSON.stringify(previewConfig)} />
        </div>
      </div>
    </div>

    {footerExtra}

    {/* Same markup/classNames as the library's own Footer (Components/Footer/index.tsx) —
        footerComponent slot, Back, Save, and the same validation-error toast. */}
    <div className="nic-tm-footer-container nic-tm-footer-container-border">
      <div style={{ paddingBottom: '10px' }}>{customisations?.footerComponent}</div>
      <div className="nic-tm-inside-main-container">
        <Button size="medium" variant="outlined" onClick={onBack}>
          Back
        </Button>
      </div>
      <Button
        size="medium"
        className="nic-footer-main-submit-button"
        disabled={disabled}
        onClick={onSave}
        data-testid="bloomreach-send-save"
      >
        {customisations?.sendButtonText || 'Send'}
      </Button>
      <Toast
        anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
        maxWidth="30vw"
        description="Provide all necessary details and make sure they're valid!"
        onClose={onCloseInvalidToast}
        title="Message missing or invalid"
        type="error"
        open={showInvalidToast}
        autoHideDuration={5000}
      />
    </div>
  </ThemeProvider>
);

export default SendPanelShell;
